import { HomeStreamEvent } from "../../services/home-ia/home-stream.service";
import { HomeDecisionActionEchoTracker } from "./home-decision-action-echo.tracker";
import { HomeDashboardStore } from "./home.component.dashboard-store";
import { HomeAutoRefreshTarget } from "./home.component.state";

export interface HomeDashboardRefreshPort {
  refreshPulse(): void;
  refreshOverview(): void;
  refreshNextBestDecision(): void;
  refreshDecisionList(): void;
  refreshCalendarDecisionState(): void;
  refreshDecisionDetail(): void;
  refreshDatesSummary(): void;
  refreshActivity(): void;
  refreshMetrics(): void;
}

export class HomeStreamEventCoordinator {
  readonly pendingTargets = new Set<HomeAutoRefreshTarget>();
  timer?: ReturnType<typeof setTimeout>;
  private readonly debounceMs = 75;

  constructor(
    private readonly store: HomeDashboardStore,
    private readonly echo: HomeDecisionActionEchoTracker,
    private readonly refresh: HomeDashboardRefreshPort
  ) {}

  handle(event: HomeStreamEvent): void {
    if (this.echo.shouldSuppress(event)) return;
    switch (event.type) {
      case "pulse.stateChanged":
      case "decision.metricsChanged":
        return this.queue("pulse", "metrics");
      case "dates.detected":
        this.queueSseRefresh("overview", "dates", "list");
        if (this.shouldRefreshSelectedDecision(event)) this.queueSseRefresh("detail");
        return;
      case "decision.listChanged":
        return this.queueSseRefresh("overview", "list");
      case "decision.changed":
        this.queueSseRefresh("overview", "list", "dates");
        if (this.shouldRefreshSelectedDecision(event)) this.queueSseRefresh("detail");
        return;
      case "decision.nextChanged":
        return this.queueSseRefresh("overview", "next");
      case "decision.activityAdded":
        this.queueSseRefresh("activity");
        if (this.shouldRefreshSelectedDecision(event)) this.queueSseRefresh("detail");
        return;
      default:
        return;
    }
  }

  shouldRefreshSelectedDecision(event: HomeStreamEvent): boolean {
    const selected = this.store.selectedDecisionIdSubject.value;
    if (!selected) return false;
    const decisionId = this.getDecisionId(event);
    return !decisionId || decisionId === selected;
  }
  getDecisionId(event: HomeStreamEvent): string | null {
    if (!event.payload || typeof event.payload !== "object") return null;
    const value = (event.payload as Record<string, unknown>)["decisionId"];
    return typeof value === "string" && value.length > 0 ? value : null;
  }
  queue(...targets: HomeAutoRefreshTarget[]): void {
    targets.forEach((target) => this.pendingTargets.add(target));
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = undefined;
      const queued = Array.from(this.pendingTargets);
      this.pendingTargets.clear();
      this.flush(queued);
    }, this.debounceMs);
  }
  queueSseRefresh(...targets: HomeAutoRefreshTarget[]): void {
    this.store.markDecisionSlicesStale(targets);
    this.queue(...targets);
  }
  flush(targets: readonly HomeAutoRefreshTarget[]): void {
    const unique = new Set(targets);
    if (unique.has("pulse")) this.refresh.refreshPulse();
    if (unique.has("overview")) this.refresh.refreshOverview();
    if (unique.has("next")) this.refresh.refreshNextBestDecision();
    if (unique.has("list")) this.refresh.refreshDecisionList();
    if (unique.has("calendar")) this.refresh.refreshCalendarDecisionState();
    if (unique.has("detail")) this.refresh.refreshDecisionDetail();
    if (unique.has("dates")) this.refresh.refreshDatesSummary();
    if (unique.has("activity")) this.refresh.refreshActivity();
    if (unique.has("metrics")) this.refresh.refreshMetrics();
  }
  dispose(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = undefined;
    this.pendingTargets.clear();
  }
}
